import React, { createContext, useContext, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { GET_CAREPLAN } from '../Queries';
import { TENANT_CONFIG_QUERY } from '../../../../components/RO/Base/TenantConfigQuery';

// GraphQL-backed CareplanDataContext: centralizes careplan data (careplan, sites) and derived selectors
// like isMRLApplicable for the workflow. Kept separate from UI/nav contexts to avoid coupling.
export const GET_SITES_WITH_MODALITY = gql`
  query siteListByCareplan($careplanId: ID!) {
    siteListByCareplan(careplanId: $careplanId) {
      id
      modality
    }
  }
`;

const isMRLSafetyScreeningApplicableFromData = (data: any): boolean => {
  const sites = data?.siteListByCareplan || [];
  return Array.isArray(sites) && sites.some((s: any) => (s?.modality || '') === '7MV FFF');
};

interface CareplanDataContextValue {
  careplan: any;
  sitesData: any;
  isMRLApplicable: boolean;
  loading: boolean;
  error: any;
}

const CareplanDataContext = createContext<CareplanDataContextValue | undefined>(undefined);

interface Props {
  careplanId: string;
  children: React.ReactNode;
}

export const CareplanDataProvider = ({ careplanId, children }: Props): JSX.Element => {
  const {
    data: careplanData,
    loading: careplanLoading,
    error: careplanError,
  } = useQuery(GET_CAREPLAN, {
    variables: { id: careplanId },
    skip: !careplanId || careplanId === 'create',
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: sitesData,
    loading: sitesLoading,
    error: sitesError,
  } = useQuery(GET_SITES_WITH_MODALITY, {
    variables: { careplanId },
    skip: !careplanId || careplanId === 'create',
    fetchPolicy: 'cache-and-network',
  });

  const { data: tenantConfigData } = useQuery(TENANT_CONFIG_QUERY);

  const value = useMemo<CareplanDataContextValue>(() => {
    const isMRLApplicable =
      tenantConfigData?.tenantConfig?.enableMrlSafetyScreening && isMRLSafetyScreeningApplicableFromData(sitesData);
    return {
      careplan: careplanData?.careplan,
      sitesData,
      isMRLApplicable,
      loading: careplanLoading || sitesLoading,
      error: careplanError || sitesError,
    };
  }, [careplanData, sitesData, careplanLoading, sitesLoading, careplanError, sitesError]);

  return <CareplanDataContext.Provider value={value}>{children}</CareplanDataContext.Provider>;
};

export const useCareplanDataContext = (): CareplanDataContextValue => {
  const ctx = useContext(CareplanDataContext);
  if (!ctx) throw new Error('useCareplanDataContext must be used within CareplanDataProvider');
  return ctx;
};
